import React from 'react';
import {
  HeartIcon,
  ChatBubbleLeftEllipsisIcon,
  InformationCircleIcon,
  PhoneIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

export interface PurposeOption {
  id: 'services' | 'medical-assistance' | 'encounter' | 'track-feedback';
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
}

interface PatientPurposeSelectorProps {
  onSelect: (purpose: PurposeOption['id']) => void;
  className?: string;
}

const purposes: PurposeOption[] = [
  {
    id: 'encounter',
    title: 'Hospital Encounter',
    description: 'You are here about a current or previous hospital visit, appointment, or medical encounter.',
    icon: ChatBubbleLeftEllipsisIcon,
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'track-feedback',
    title: 'Track Your Feedback',
    description: 'Check the status of your submitted feedback using your tracking number. View responses and updates from our staff.',
    icon: MagnifyingGlassIcon,
    color: 'from-purple-500 to-indigo-500'
  },
  {
    id: 'services',
    title: 'Hospital Services Information',
    description: 'Learn about our medical services, departments, facilities, and general information about our hospital.',
    icon: InformationCircleIcon,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'medical-assistance',
    title: 'Medical Assistance',
    description: 'Get help with medical emergencies, urgent care needs, or contact our medical staff directly.',
    icon: PhoneIcon,
    color: 'from-red-500 to-pink-500'
  }
];

export const PatientPurposeSelector: React.FC<PatientPurposeSelectorProps> = ({ 
  onSelect, 
  className = '' 
}) => {
  return (
    <div className={`space-y-6 ${className}`}>
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          How can we help you today?
        </h2>
        <p className="text-lg text-gray-900 max-w-2xl mx-auto">
          Please select the option that best describes why you're visiting our patient portal.
        </p>
      </div>

      <div className="grid gap-6 max-w-4xl mx-auto">
        {purposes.map((purpose) => {
          const IconComponent = purpose.icon;
          
          return (
            <button
              key={purpose.id}
              onClick={() => onSelect(purpose.id)}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-left transform hover:-translate-y-1 border border-gray-100 hover:border-gray-200"
            >
              <div className="flex items-start space-x-6">
                <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-r ${purpose.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {purpose.title}
                  </h3>
                  <p className="text-gray-900 text-base leading-relaxed">
                    {purpose.description}
                  </p>
                </div>

                <div className="flex-shrink-0">
                  <svg 
                    className="w-6 h-6 text-gray-700 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="text-center mt-8">
        <p className="text-sm text-gray-800">
          Need immediate emergency assistance? Call <span className="font-semibold text-red-600">(555) 911-CARE</span>
        </p>
      </div>
    </div>
  );
};